/**
 * Portal-only filter cascade for the Reports tab.
 * Hits `/v1/{b2b|client}/reporter/filter/*` — not the public `/api/v1` Reporting API.
 * Intentionally separate from `reporting.endpoints.ts` (old-dashboard).
 */
import { invisiblyApi } from 'src/redux/api/invisibly-api';
import ReduxService from 'src/redux/redux.service';
import { ReduxState } from 'src/redux/redux-state';
import { InvisiblyApiResponse } from 'src/services/api/api-response.types';
import {
  ReportingPortalAccountsResponse,
  ReportingPortalCampaignsResponse,
  ReportingPortalFilterEntity,
  ReportingPortalLineItemsResponse,
} from './reporting-portal-filters.types';

const sanitizeClientId = (value?: string | null): string | undefined => {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return undefined;
  return trimmed;
};

const getIsClientView = (): boolean => {
  const state = ReduxService.Store.getState() as ReduxState;
  const userGroups = state?.userState?.permissions?.groups || [];
  return userGroups.includes('client_user') || userGroups.includes('client_admin');
};

const getApiPrefixAndParams = (previewClientId?: string | null) => {
  const clientId = sanitizeClientId(previewClientId);
  if (clientId) {
    return { prefix: 'client', params: { ext_client_id: clientId } };
  }
  const isClient = getIsClientView();
  return { prefix: isClient ? 'client' : 'b2b', params: {} as Record<string, string> };
};

const reportingPortalFiltersEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getReportingPortalSources: builder.query<string[], { previewClientId?: string } | void>({
      query: (arg) => {
        const { prefix, params } = getApiPrefixAndParams(arg?.previewClientId);
        return {
          method: 'get',
          url: `${prefix}/reporter/filter/sources`,
          ...(Object.keys(params).length ? { params } : {}),
        };
      },
      transformResponse(response: InvisiblyApiResponse<{ sources: string[] }>) {
        return response.item.sources;
      },
    }),

    getReportingPortalAccounts: builder.query<
      ReportingPortalFilterEntity[],
      { source: string; previewClientId?: string }
    >({
      query: ({ source, previewClientId }) => {
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          method: 'get',
          url: `${prefix}/reporter/filter/sources/${source}/accounts`,
          ...(Object.keys(params).length ? { params } : {}),
        };
      },
      transformResponse(response: InvisiblyApiResponse<ReportingPortalAccountsResponse>) {
        return response.item.account ?? [];
      },
    }),

    getReportingPortalCampaigns: builder.query<
      ReportingPortalFilterEntity[],
      { source: string; accountId: Array<string | number>; previewClientId?: string }
    >({
      query: ({ source, accountId, previewClientId }) => {
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          method: 'get',
          url: `${prefix}/reporter/filter/sources/${source}/accounts/${accountId}/campaigns`,
          ...(Object.keys(params).length ? { params } : {}),
        };
      },
      transformResponse(response: InvisiblyApiResponse<ReportingPortalCampaignsResponse>) {
        return response.item.campaigns ?? [];
      },
    }),

    getReportingPortalLineItems: builder.query<
      ReportingPortalFilterEntity[],
      {
        source: string;
        accountId: Array<string | number>;
        campaignId: Array<string | number>;
        previewClientId?: string;
      }
    >({
      query: ({ source, accountId, campaignId, previewClientId }) => {
        const { prefix, params } = getApiPrefixAndParams(previewClientId);
        return {
          method: 'get',
          url: `${prefix}/reporter/filter/sources/${source}/accounts/${accountId}/campaigns/${campaignId}/lines`,
          ...(Object.keys(params).length ? { params } : {}),
        };
      },
      transformResponse(response: InvisiblyApiResponse<ReportingPortalLineItemsResponse>) {
        return response.item.line_items ?? [];
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetReportingPortalSourcesQuery,
  useGetReportingPortalAccountsQuery,
  useGetReportingPortalCampaignsQuery,
  useGetReportingPortalLineItemsQuery,
} = reportingPortalFiltersEndpoints;
